import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-muted-light">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-card rounded-xl shadow-soft p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-gentle"
              >
                <Icon name="ArrowLeft" size={20} className="text-muted-foreground" />
              </button>
              <h1 className="text-3xl font-heading font-semibold text-foreground">
                Privacy Policy
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Last updated: {new Date()?.toLocaleDateString()}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-slate max-w-none">
            <div className="space-y-8">
              
              {/* Introduction */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  1. Introduction
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  We are committed to protecting your privacy and personal data. This Privacy Policy 
                  explains how we collect, use, process, and protect your information when you use 
                  our holistic wellness directory platform.
                </p>
              </section>

              {/* Information We Collect */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  2. Information We Collect
                </h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Personal Information</h3>
                  <p className="text-muted-foreground font-body leading-relaxed">
                    We collect information you provide directly, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>Name and contact information (email, phone, address)</li>
                    <li>Account credentials and profile information</li>
                    <li>Business information (for practitioners)</li>
                    <li>Communication preferences</li>
                    <li>Reviews, comments, and other user-generated content</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-foreground mt-6">Usage Information</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>How you interact with our platform</li>
                    <li>Pages visited and features used</li>
                    <li>Search queries and preferences</li>
                    <li>Device information and browser type</li>
                    <li>IP address and location data</li>
                  </ul>
                </div>
              </section>

              {/* How We Use Information */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  3. How We Use Your Information
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    We use your information to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>Provide and improve our services</li>
                    <li>Create and manage your account</li>
                    <li>Facilitate connections between clients and practitioners</li>
                    <li>Send important updates and notifications</li>
                    <li>Personalize your experience and recommendations</li>
                    <li>Respond to your inquiries and provide customer support</li>
                    <li>Prevent fraud and ensure platform security</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </div>
              </section>

              {/* Information Sharing */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  4. How We Share Your Information
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    We do not sell your personal information. We may share information in the 
                    following circumstances:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>With practitioners when you book appointments or make inquiries</li>
                    <li>With service providers who help us operate our platform</li>
                    <li>When required by law or to protect our rights</li>
                    <li>In connection with business transfers or acquisitions</li>
                    <li>With your explicit consent</li>
                  </ul>
                </div>
              </section>

              {/* Data Storage and Security */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  5. Data Storage and Security
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    We implement appropriate security measures to protect your information:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security audits and updates</li>
                    <li>Limited access to personal data by authorized personnel</li>
                    <li>Secure data centers and backup systems</li>
                    <li>Account security features like password protection</li>
                  </ul>
                </div>
              </section>

              {/* Data Retention */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  6. Data Retention
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  We retain your personal information only as long as necessary to provide our 
                  services and fulfill the purposes outlined in this policy. When you delete your 
                  account, we will remove your personal information, though some information may 
                  be retained for legal or security purposes.
                </p>
              </section>

              {/* Your Rights */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  7. Your Rights and Choices
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    Depending on your location, you may have the following rights:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>Access to your personal information</li>
                    <li>Correction of inaccurate information</li>
                    <li>Deletion of your personal information</li>
                    <li>Data portability</li>
                    <li>Restriction of processing</li>
                    <li>Objection to certain types of processing</li>
                    <li>Withdrawal of consent</li>
                  </ul>
                  
                  <p className="text-muted-foreground font-body leading-relaxed mt-4">
                    To exercise these rights, please contact us using the information provided below.
                  </p>
                </div>
              </section>

              {/* Cookies and Tracking */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  8. Cookies and Tracking Technologies
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    We use cookies and similar technologies to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>Remember your preferences and settings</li>
                    <li>Analyze platform usage and performance</li>
                    <li>Provide personalized content and recommendations</li>
                    <li>Measure the effectiveness of our marketing</li>
                  </ul>
                  <p className="text-muted-foreground font-body leading-relaxed">
                    You can control cookie settings through your browser preferences.
                  </p>
                </div>
              </section>

              {/* Third-Party Services */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  9. Third-Party Services
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  Our platform may contain links to third-party websites or services. We are not 
                  responsible for the privacy practices of these third parties. We encourage you 
                  to read their privacy policies before providing any personal information.
                </p>
              </section>

              {/* International Transfers */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  10. International Data Transfers
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  Your information may be transferred to and processed in countries other than your 
                  residence. We ensure appropriate safeguards are in place to protect your 
                  information in accordance with applicable data protection laws.
                </p>
              </section>

              {/* Children's Privacy */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  11. Children's Privacy
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  Our platform is not intended for children under 16 years of age. We do not 
                  knowingly collect personal information from children under 16. If we become 
                  aware that we have collected such information, we will delete it promptly.
                </p>
              </section>

              {/* Changes to Policy */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  12. Changes to This Privacy Policy
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any 
                  material changes by posting the new policy on our platform and updating the 
                  "last updated" date. Your continued use of our services after changes become 
                  effective constitutes acceptance of the new policy.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  13. Contact Us
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  If you have questions about this Privacy Policy or our data practices, please 
                  contact us at:
                </p>
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <p className="text-muted-foreground font-body">
                    <strong>Data Protection Officer</strong><br/>
                    <strong>Email:</strong> privacy@holisticdirectory.com<br/>
                    <strong>Address:</strong> Privacy Department<br/>
                    Holistic Wellness Directory<br/>
                    123 Wellness Street<br/>
                    London, UK
                  </p>
                </div>
              </section>

            </div>
          </div>

          {/* Footer Actions */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Go Back
              </Button>
              <Button
                variant="default"
                onClick={() => navigate('/register')}
                iconName="UserPlus"
                iconPosition="left"
              >
                Create Account
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;